import React, { useState, useEffect, useCallback } from 'react';
import APIService from '../../services/api';
import { confirmErpia } from '../ConfirmDialog';

/**
 * Facturiers (affacturage) — qui encaisse la facture.
 *
 * Quand les créances sont cédées à une société d'affacturage (A.B.S. Factoring
 * pour TAT), la facture doit porter SES coordonnées bancaires et la mention de
 * cession : un client qui paie sur le compte de l'agence ne se libère pas de sa
 * dette. Ces informations ne s'inventent pas au moment d'éditer une facture —
 * elles se règlent ici, une fois, et le serveur les imprime sur les factures.
 *
 * Routes : GET/POST/PUT/DELETE /config/facturiers. Absentes (404/405/501) :
 * l'écran le dit et n'écrit rien.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

const LBL = {
  fontFamily: 'var(--erp-font-mono)', fontSize: '9.5px', fontWeight: 700,
  letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--erp-text-tertiary)', marginBottom: '5px',
};
const VIDE = { nom: '', adresse: '', iban: '', bic: '', mention: '', actif: false };

/** IBAN lisible : groupes de 4, comme sur un relevé. */
const ibanLisible = (v) => String(v || '').replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();

export default function CRMFacturiers({ showNotification, onListe }) {
  const [liste, setListe] = useState(null);      // null = chargement
  const [absent, setAbsent] = useState(false);
  const [erreur, setErreur] = useState('');
  const [form, setForm] = useState(null);        // { ...facturier } en cours d'édition
  const [busy, setBusy] = useState(false);
  // Refus du serveur accroché AU CHAMP fautif (bordure + message dessous),
  // jamais une notification qui s'évapore : c'est là qu'on corrige.
  const [champErreur, setChampErreur] = useState(null);   // { champ: 'iban'|'nom', message }
  const ibanRef = React.useRef(null);

  // Le parent (les Paramètres du CRM) affiche le compte qui encaisse : il lit
  // la MÊME liste que cet écran, sinon le bandeau resterait sur l'ancien
  // facturier après une modification.
  const onListeRef = React.useRef(onListe);
  useEffect(() => { onListeRef.current = onListe; }, [onListe]);

  const charger = useCallback(async () => {
    setErreur('');
    try {
      const r = await fetch(`${BASE}/config/facturiers`, { headers: authH() });
      if ([404, 405, 501].includes(r.status)) { setAbsent(true); setListe([]); onListeRef.current?.([]); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      const arr = j.data || j.facturiers || (Array.isArray(j) ? j : []);
      setListe(Array.isArray(arr) ? arr : []);
      onListeRef.current?.(Array.isArray(arr) ? arr : []);
    } catch (e) { setErreur(e.message); setListe([]); onListeRef.current?.([]); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const enregistrer = async () => {
    if (!form?.nom?.trim() || !form?.iban?.trim()) return;
    setBusy(true);
    setChampErreur(null);
    try {
      const corps = {
        nom: form.nom.trim(), adresse: form.adresse.trim(), bic: form.bic.trim().toUpperCase(),
        iban: form.iban.replace(/\s+/g, '').toUpperCase(), mention: form.mention.trim(), actif: !!form.actif,
      };
      const edition = form.id != null;
      const r = await fetch(`${BASE}/config/facturiers${edition ? `/${form.id}` : ''}`, {
        method: edition ? 'PUT' : 'POST', headers: jsonH(), body: JSON.stringify(corps),
      });
      const j = await r.json().catch(() => ({}));
      if ([404, 405, 501].includes(r.status) && !j.error) {
        showNotification?.('L\'enregistrement des facturiers arrive côté serveur — rien n\'a été enregistré.', 'info');
        return;
      }
      if (!r.ok || j.success === false) {
        const msg = j.error || j.message || `HTTP ${r.status}`;
        // Un 400 parle presque toujours de l'IBAN (forme ou clé de contrôle) :
        // le message part au champ, qui prend la bordure et le focus.
        if (r.status === 400) {
          const champ = /iban/i.test(msg) || !/nom|raison/i.test(msg) ? 'iban' : 'nom';
          setChampErreur({ champ, message: msg });
          if (champ === 'iban') ibanRef.current?.focus();
          return;
        }
        throw new Error(msg);
      }
      showNotification?.(j.message || (edition ? 'Facturier mis à jour.' : 'Facturier ajouté.'), 'success');
      setForm(null);
      // Passer un facturier en « par défaut » désactive les autres côté
      // serveur : la liste se relit, sinon deux cartes s'afficheraient actives.
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const supprimer = async (f) => {
    const ok = await confirmErpia({
      title: 'Retirer ce facturier',
      message: `${f.nom} ne sera plus proposé sur les factures.`,
      detail: 'Les brouillons qui l\'utilisent repasseront sur vos coordonnées bancaires. Les factures déjà émises, elles, gardent celles avec lesquelles elles sont parties.',
      confirmLabel: 'Retirer le facturier',
    });
    if (!ok) return;
    try {
      const r = await fetch(`${BASE}/config/facturiers/${f.id}`, { method: 'DELETE', headers: authH() });
      const j = await r.json().catch(() => ({}));
      if ([404, 405, 501].includes(r.status) && !j.error) {
        showNotification?.('La suppression arrive côté serveur — rien n\'a été retiré.', 'info');
        return;
      }
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Facturier retiré.', 'success');
      // Le serveur dit ce que le retrait a emporté : les brouillons qui
      // repassent sur nos coordonnées (par leur numéro) et les factures déjà
      // émises, qui gardent les leurs. On le répète, sinon un brouillon change
      // de compte bancaire sans que personne le sache.
      const detaches = Array.isArray(j.brouillons_detaches) ? j.brouillons_detaches : [];
      const emises = Number(j.factures_emises ?? 0) || 0;
      if (detaches.length) {
        showNotification?.(`${detaches.length} brouillon${detaches.length > 1 ? 's' : ''} repasse${detaches.length > 1 ? 'nt' : ''} sur vos coordonnées : ${detaches.join(', ')}.`, 'info');
      }
      if (emises) {
        showNotification?.(`${emises} facture${emises > 1 ? 's' : ''} déjà émise${emises > 1 ? 's' : ''} garde${emises > 1 ? 'nt' : ''} les coordonnées avec lesquelles elle${emises > 1 ? 's sont parties' : ' est partie'}.`, 'info');
      }
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.6, color: 'var(--erp-text-secondary)', maxWidth: '72ch' }}>
        Quand vos créances sont cédées à une société d'affacturage, la facture doit porter <strong style={{ color: 'var(--erp-ink)' }}>ses
        coordonnées bancaires</strong> et la mention de cession : un client qui paie sur votre compte ne se libère pas de sa dette.
        Réglez-le ici une fois — le serveur l'imprime ensuite sur les factures.
      </p>

      {absent && (
        <div style={{ padding: '11px 13px', border: '1px solid var(--erp-line)', borderRadius: 'var(--erp-radius-sm)', background: 'var(--erp-surface-muted)', fontSize: '12.5px', color: 'var(--erp-text-secondary)' }}>
          Les facturiers arrivent côté serveur. L'écran est prêt : vos saisies aboutiront dès la mise en ligne, rien n'est enregistré d'ici là.
        </div>
      )}
      {erreur && <div style={{ padding: '11px 13px', border: '1px solid var(--danger-bd)', borderRadius: 'var(--erp-radius-sm)', background: 'var(--danger-bg)', color: 'var(--danger-fg)', fontSize: '12.5px' }}>{erreur}</div>}

      {liste === null ? (
        <div style={{ padding: '18px', textAlign: 'center', color: 'var(--erp-text-tertiary)', fontSize: '13px' }}><BI icon="arrow-repeat" /> Chargement…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
          {liste.map(f => (
            <div key={f.id} style={{ border: `1px solid ${f.actif ? 'var(--erp-accent)' : 'var(--erp-line)'}`, borderRadius: 'var(--erp-radius-md)', padding: '13px 15px', background: 'var(--erp-surface)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: '14px', color: 'var(--erp-ink-strong)' }}>{f.nom}</strong>
                {f.actif && <span className="erp-chip" style={{ background: 'var(--erp-accent-soft)', borderColor: 'var(--erp-accent)', color: 'var(--erp-accent)' }}>Utilisé par défaut</span>}
                <span style={{ flex: 1 }} />
                <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setForm({ ...VIDE, ...f })}><BI icon="pencil" /> Modifier</button>
                <button type="button" className="erp-btn erp-btn--danger erp-btn--sm" onClick={() => supprimer(f)}><BI icon="trash3" /> Retirer</button>
              </div>
              {f.adresse && <div style={{ marginTop: '4px', fontSize: '12.5px', color: 'var(--erp-text-secondary)' }}>{f.adresse}</div>}
              <div className="erp-mono" style={{ marginTop: '6px', fontSize: '12px', color: 'var(--erp-ink)' }}>
                {ibanLisible(f.iban)}{f.bic ? ` · ${f.bic}` : ''}
              </div>
              {f.mention && <p style={{ margin: '8px 0 0', fontSize: '11.5px', lineHeight: 1.55, color: 'var(--erp-text-tertiary)' }}>{f.mention}</p>}
            </div>
          ))}
          {!liste.length && !absent && (
            <div style={{ padding: '20px', border: '1px dashed var(--erp-field-border)', borderRadius: 'var(--erp-radius-md)', textAlign: 'center', color: 'var(--erp-text-secondary)', fontSize: '12.5px' }}>
              Aucun facturier. Vos factures portent vos propres coordonnées bancaires.
            </div>
          )}
        </div>
      )}

      {form ? (
        <div style={{ border: '1px solid var(--erp-line)', borderRadius: 'var(--erp-radius-md)', padding: '15px 17px', background: 'var(--erp-surface)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--erp-ink-strong)' }}>{form.id != null ? 'Modifier le facturier' : 'Nouveau facturier'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
            <label>
              <div style={LBL}>Raison sociale *</div>
              <input className="erp-field" style={{ width: '100%' }} value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} placeholder="A.B.S. Factoring AG" />
            </label>
            <label>
              <div style={LBL}>Adresse</div>
              <input className="erp-field" style={{ width: '100%' }} value={form.adresse} onChange={e => setForm(f => ({ ...f, adresse: e.target.value }))} placeholder="Zürcherstrasse 104, CH-8852 Altendorf" />
            </label>
            <label>
              <div style={LBL}>IBAN *</div>
              <input ref={ibanRef} className="erp-field erp-mono" style={{ width: '100%', ...(champErreur?.champ === 'iban' ? { borderColor: 'var(--danger-bd)' } : null) }}
                aria-invalid={champErreur?.champ === 'iban' || undefined}
                value={form.iban} onChange={e => { setForm(f => ({ ...f, iban: e.target.value })); if (champErreur?.champ === 'iban') setChampErreur(null); }}
                placeholder="CH26 0883 6300 0064 0200 6" />
              {champErreur?.champ === 'iban' && (
                <span role="alert" style={{ display: 'block', marginTop: '5px', fontSize: '11.5px', color: 'var(--danger-fg)' }}>{champErreur.message}</span>
              )}
            </label>
            <label>
              <div style={LBL}>BIC</div>
              <input className="erp-field erp-mono" style={{ width: '100%' }} value={form.bic} onChange={e => setForm(f => ({ ...f, bic: e.target.value }))} placeholder="COBACHZHXXX" />
            </label>
          </div>
          <label>
            <div style={LBL}>Mention imprimée sur la facture</div>
            <textarea className="erp-field" rows={5} style={{ width: '100%', resize: 'vertical', lineHeight: 1.55 }}
              value={form.mention} onChange={e => setForm(f => ({ ...f, mention: e.target.value }))}
              placeholder="En vertu d'un contrat d'affacturage, toutes les créances existantes et futures de … sont cédées à …" />
            <div style={{ marginTop: '5px', fontSize: '11.5px', color: 'var(--erp-text-tertiary)' }}>
              Texte repris mot pour mot sur la facture. C'est lui qui rend le paiement libératoire : reprenez celui de votre contrat d'affacturage.
            </div>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '13px', color: 'var(--erp-ink)' }}>
            <input type="checkbox" checked={!!form.actif} onChange={e => setForm(f => ({ ...f, actif: e.target.checked }))} style={{ accentColor: 'var(--erp-accent)' }} />
            Utiliser ce facturier par défaut sur les nouvelles factures
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px' }}>
            <button type="button" className="erp-btn erp-btn--secondary" onClick={() => setForm(null)}>Annuler</button>
            <button type="button" className="erp-btn erp-btn--primary" onClick={enregistrer} disabled={busy || !form.nom.trim() || !form.iban.trim()}
              style={(busy || !form.nom.trim() || !form.iban.trim()) ? { opacity: 0.5 } : undefined}>
              {busy ? 'Enregistrement…' : 'Enregistrer le facturier'}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <button type="button" className="erp-btn erp-btn--primary" onClick={() => setForm({ ...VIDE })}>
            <BI icon="plus-lg" /> Ajouter un facturier
          </button>
        </div>
      )}
    </div>
  );
}
